import { yupResolver } from "@hookform/resolvers/yup";
import { Box } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { AppRoutes } from "../../../constants";
import { useCategories } from "../../../hooks/useCategories";
import { useCities } from "../../../hooks/useCities";
import useOrganizationCities from "../../../hooks/useOrganizationCities";
import {
  createOrganization_V2,
  getOrganizationById_V2,
  updateOrganization_V2,
} from "../../../Services_V2/Organization.service";
import { showError } from "../../../utils/error";
import { BankAccountType } from "../../AddEditNGOPage/const";
import {
  OrganizationFormValues,
  OrgFormDataFields,
  registerOrganizationSchema,
  updateOrgSchema,
} from "./const";
import styles from "./CreateEditOrganization.module.css";
import OrganizationForm from "./OrganizationForm";

interface Props {
  organizationId?: string;
  settingOrgId?: string;
}

const CreateEditOrganization: React.FC<Props> = ({ settingOrgId }) => {
  const navigate = useNavigate();
  const { organizationId } = useParams<{ organizationId?: string }>();
  const resolvedOrgId = settingOrgId || organizationId;
  const isEdit = !!resolvedOrgId;
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [cert80GPreview, setCert80GPreview] = useState<string | null>(null);
  const [trustDeedPreview, setTrustDeedPreview] = useState<string | null>(null);
  const [donationQrPreview, setDonationQrPreview] = useState<string | null>(
    null
  );
  const { options: cityOptions } = useCities();
  const { options: categoryOptions } = useCategories();
  const { cities: orgCities, refetch: refetchOrgCities } =
    useOrganizationCities(isEdit ? String(resolvedOrgId) : undefined);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<OrganizationFormValues>({
    resolver: yupResolver(
      isEdit ? updateOrgSchema : registerOrganizationSchema
    ),
    defaultValues: {
      name: "",
      title: "",
      description: "",
      email: "",
      tagline: "",
      websiteUrl: "",
      darpanId: "",
      logo: null,
      donationQr: null,
      eightyGCertificate: null,
      trustDeedCertificate: null,
      cities: [],
      categories: [],
      bankDetails: {
        bankName: "",
        branch: "",
        ifscCode: "",
        accountType: BankAccountType.SAVINGS,
        beneficiaryName: "",
        accountNumber: "",
        upiId: "",
      },
      ...(!isEdit && {
        contact_person: {
          name: "",
          email: "",
          number: "",
        },
      }),
    },
  });

  const fetchOrganization = useCallback(async () => {
    if (!resolvedOrgId) return;

    try {
      setFetching(true);
      const res = await getOrganizationById_V2(resolvedOrgId);
      const data = res?.data;

      if (!data) {
        toast.error("Organization not found");
        navigate(AppRoutes.ADMIN.ORGANIZATIONS.LIST);
        return;
      }

      // Set form values
      setValue("name", data.name ?? "");
      setValue("title", data.title ?? "");
      setValue("description", data.description ?? "");
      setValue("email", data.email ?? "");
      setValue("tagline", data.tagline ?? "");
      setValue("websiteUrl", data.websiteUrl ?? "");
      setValue("darpanId", data.darpanId ?? "");
      setValue("contact_person.name", data.contact_person?.name ?? "");
      setValue("contact_person.email", data.contact_person?.email ?? "");
      setValue("contact_person.number", data.contact_person?.number ?? "");
      setValue(
        "categories",
        data.categories?.map((c: any) => c._id || c) || []
      );
      setValue("bankDetails.bankName", data.bankDetails?.bankName ?? "");
      setValue("bankDetails.branch", data.bankDetails?.branch ?? "");
      setValue("bankDetails.ifscCode", data.bankDetails?.ifscCode ?? "");
      setValue(
        "bankDetails.accountType",
        (data.bankDetails?.accountType as BankAccountType) ??
          BankAccountType.SAVINGS
      );
      setValue(
        "bankDetails.beneficiaryName",
        data.bankDetails?.beneficiaryName ?? ""
      );
      setValue(
        "bankDetails.accountNumber",
        data.bankDetails?.accountNumber ?? ""
      );
      setValue("bankDetails.upiId", data.bankDetails?.upiId ?? "");

      if (data.logo?.url) {
        setLogoPreview(data.logo.url);
      }
      if (data.donationQr?.url) {
        setDonationQrPreview(data.donationQr.url);
      }
      if (data.eightyGCertificate?.url) {
        setCert80GPreview(data.eightyGCertificate.url);
      }
      if (data.trustDeedCertificate?.url) {
        setTrustDeedPreview(data.trustDeedCertificate.url);
      }
    } catch (error) {
      showError(error);
      toast.error("Failed to load organization");
      navigate(AppRoutes.ADMIN.ORGANIZATIONS.LIST);
    } finally {
      setFetching(false);
    }
  }, [resolvedOrgId, setValue, navigate]);

  useEffect(() => {
    fetchOrganization();
    if (isEdit) {
      refetchOrgCities?.();
    }
  }, [fetchOrganization]);

  useEffect(() => {
    if (isEdit && orgCities?.length) {
      setValue(
        "cities",
        orgCities
          ?.filter((c: any) => c) // Filter out null/undefined
          ?.map((c: any) =>
            typeof c === "object" ? String(c._id) : String(c)
          ) || []
      );
    }
  }, [isEdit, orgCities, setValue]);

  // Cleanup blob URLs
  useEffect(() => {
    return () => {
      [
        logoPreview,
        cert80GPreview,
        trustDeedPreview,
        donationQrPreview,
      ].forEach((url) => {
        if (url && url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [logoPreview, cert80GPreview, trustDeedPreview, donationQrPreview]);

  const onSubmit = async (values: any) => {
    setLoading(true);
    try {
      const basePayload = {
        name: values.name,
        title: values.title,
        description: values.description,
        email: values.email,
        tagline: values.tagline,
        websiteUrl: values.websiteUrl,
        darpanId: values.darpanId,
        cities: values.cities,
        categories: values.categories,
        ...(values.bankDetails && {
          bankDetails: {
            bankName: values.bankDetails.bankName || "",
            branch: values.bankDetails.branch || "",
            ifscCode: values.bankDetails.ifscCode || "",
            accountType: values.bankDetails.accountType || "",
            beneficiaryName: values.bankDetails.beneficiaryName || "",
            accountNumber: values.bankDetails.accountNumber || "",
            upiId: values.bankDetails.upiId || "",
          },
        }),
      };

      if (isEdit) {
        const res = await updateOrganization_V2(resolvedOrgId!, basePayload);
        toast.success(res?.message || "Organization updated successfully");
      } else {
        const formData = new FormData();
        formData.append(OrgFormDataFields.NAME, values.name);
        formData.append(OrgFormDataFields.TITLE, values.title);
        formData.append(OrgFormDataFields.DESCRIPTION, values.description);
        formData.append(OrgFormDataFields.EMAIL, values.email);
        formData.append(OrgFormDataFields.TAGLINE, values.tagline);
        if (values.websiteUrl)
          formData.append(OrgFormDataFields.WEBSITE_URL, values.websiteUrl);
        formData.append(OrgFormDataFields.DARPAN_ID, values.darpanId);

        values.cities?.forEach((city: any) =>
          formData.append(OrgFormDataFields.CITIES, city)
        );

        values.categories?.forEach((category: any) =>
          formData.append(OrgFormDataFields.CATEGORIES, category)
        );

        formData.append(
          OrgFormDataFields.CONTACT_NAME,
          values.contact_person?.name ?? ""
        );
        formData.append(
          OrgFormDataFields.CONTACT_EMAIL,
          values.contact_person?.email ?? ""
        );
        formData.append(
          OrgFormDataFields.CONTACT_NUMBER,
          values.contact_person?.number ?? ""
        );
        if (values.bankDetails.bankName) {
          formData.append("bankDetails[bankName]", values.bankDetails.bankName);
        }
        if (values.bankDetails.branch) {
          formData.append("bankDetails[branch]", values.bankDetails.branch);
        }
        if (values.bankDetails.ifscCode) {
          formData.append("bankDetails[ifscCode]", values.bankDetails.ifscCode);
        }
        if (values.bankDetails.accountType)
          formData.append(
            "bankDetails[accountType]",
            values.bankDetails.accountType
          );
        if (values.bankDetails.beneficiaryName)
          formData.append(
            "bankDetails[beneficiaryName]",
            values.bankDetails.beneficiaryName
          );
        if (values.bankDetails.accountNumber)
          formData.append(
            "bankDetails[accountNumber]",
            values.bankDetails.accountNumber
          );
        if (values.bankDetails.upiId) {
          formData.append("bankDetails[upiId]", values.bankDetails.upiId);
        }

        if (values.logo instanceof File) {
          formData.append(OrgFormDataFields.LOGO, values.logo);
        }
        if (values.eightyGCertificate instanceof File) {
          formData.append(
            OrgFormDataFields.EIGHTY_G_CERTIFICATE,
            values.eightyGCertificate
          );
        }
        if (values.trustDeedCertificate instanceof File) {
          formData.append(
            OrgFormDataFields.TRUST_DEED_CERTIFICATE,
            values.trustDeedCertificate
          );
        }

        const res = await createOrganization_V2(formData);
        toast.success(res?.message ?? "Organization created successfully");
      }

      {
        !settingOrgId && navigate(AppRoutes.ADMIN.ORGANIZATIONS.LIST);
      }
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return <Box className={styles.container}>Loading organization...</Box>;
  }

  return (
    <OrganizationForm
      isEdit={isEdit}
      control={control}
      register={register}
      errors={errors}
      cityOptions={cityOptions}
      categoryOptions={categoryOptions}
      logoPreview={logoPreview}
      cert80GPreview={cert80GPreview}
      trustDeedPreview={trustDeedPreview}
      donationQrPreview={donationQrPreview}
      setLogoPreview={setLogoPreview}
      setCert80GPreview={setCert80GPreview}
      setTrustDeedPreview={setTrustDeedPreview}
      setDonationQrPreview={setDonationQrPreview}
      resolvedOrgId={resolvedOrgId}
      onSubmit={handleSubmit(onSubmit)}
      loading={loading}
    />
  );
};

export default CreateEditOrganization;
